import React, { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ScrollView, TouchableOpacity, View } from 'react-native'
import ReanimatedSwipeable, {
  SwipeableMethods,
} from 'react-native-gesture-handler/ReanimatedSwipeable'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { useNavigation } from '@react-navigation/native'
import { NavigationContainer } from '@screens/Common/containers'
import { Icon, Pill, Text } from '@components/base'
import { chatbotQuickPromptsSelector } from '@screens/Nora/models/nora.selectors'
import { StyleService, useStyleSheet } from '@style/service'
import { ChatbotQuickPrompt, UiPillType } from '@src/types'
import { useLazyScreenPreload } from '@navigation/utils'
import { useGoBack } from '@src/utils/navigation'
import { useNoraStreamChat } from '../context/NoraStreamChatContext'

export const NoraQuickPromptsScreen = () => {
  const styles = useStyleSheet(themedStyle)
  const insets = useSafeAreaInsets()
  const navigation = useNavigation()
  const goBack = useGoBack()
  const dispatch = useDispatch()
  const quickPrompts = useSelector(chatbotQuickPromptsSelector)
  const { onAsk } = useNoraStreamChat()
  const swipeableRefs = useRef<Map<string, React.RefObject<SwipeableMethods | null>>>(new Map())

  const getSwipeableRef = (id: string) => {
    let ref = swipeableRefs.current.get(id)
    if (!ref) {
      ref = React.createRef<SwipeableMethods | null>()
      swipeableRefs.current.set(id, ref)
    }
    return ref
  }

  useEffect(() => {
    const liveIds = new Set(quickPrompts.map((prompt) => prompt.id))
    swipeableRefs.current.forEach((_, id) => {
      if (!liveIds.has(id)) {
        swipeableRefs.current.delete(id)
      }
    })
  }, [quickPrompts])

  useLazyScreenPreload('UpsertQuickPromptModal')

  const handleAddPrompt = () => {
    navigation.navigate('UpsertQuickPromptModal')
  }

  const handleEditPrompt = (prompt: ChatbotQuickPrompt) => {
    navigation.navigate('UpsertQuickPromptModal', {
      id: prompt.id,
      title: prompt.title,
      text: prompt.text,
    })
  }

  const handleSelectPrompt = (prompt: ChatbotQuickPrompt) => {
    onAsk({ prompt: prompt.text, quickPromptId: prompt.id })
  }

  const handleDeletePrompt = useCallback(
    (prompt: ChatbotQuickPrompt) => {
      swipeableRefs.current.get(prompt.id)?.current?.close()
      dispatch({ type: 'nora/deleteChatbotQuickPrompt', payload: { id: prompt.id } })
    },
    [dispatch],
  )

  const renderRightActions = useCallback(
    (prompt: ChatbotQuickPrompt) => (
      <TouchableOpacity
        style={styles.deleteAction}
        accessibilityLabel={`Delete quick prompt ${prompt.title}`}
        onPress={() => handleDeletePrompt(prompt)}
      >
        <Icon name="trash" size="24" style={styles.deleteIcon} />
      </TouchableOpacity>
    ),
    [handleDeletePrompt, styles.deleteAction, styles.deleteIcon],
  )

  return (
    <NavigationContainer title="Quick Prompts" goBack={goBack}>
      <ScrollView
        style={styles.contentContainer}
        contentContainerStyle={{ padding: 16, paddingBottom: insets.bottom }}
      >
        <View style={styles.promptsContainer}>
          {quickPrompts.map((prompt) => (
            <ReanimatedSwipeable
              key={prompt.id}
              ref={getSwipeableRef(prompt.id)}
              renderRightActions={() => renderRightActions(prompt)}
              overshootRight={false}
            >
              <TouchableOpacity
                style={[styles.promptCard, styles.row]}
                accessibilityLabel={prompt.title}
                onPress={() => handleSelectPrompt(prompt)}
              >
                <View style={styles.promptContent}>
                  <View style={[styles.title, styles.row]}>
                    <Text type="regular" numberOfLines={1}>
                      {prompt.title}
                    </Text>
                    {prompt.custom && (
                      <Pill
                        text="CUSTOM"
                        size="s"
                        textType="small"
                        textBold={false}
                        status={UiPillType.Success}
                      />
                    )}
                  </View>
                  <Text type="small" style={styles.promptText} numberOfLines={1}>
                    {prompt.text}
                  </Text>
                  {prompt.custom && (
                    <Text
                      type="small"
                      bold
                      style={styles.link}
                      onPress={() => handleEditPrompt(prompt)}
                    >
                      Edit
                    </Text>
                  )}
                </View>
                <Icon name="caret-right" size="16" />
              </TouchableOpacity>
            </ReanimatedSwipeable>
          ))}
        </View>
        <TouchableOpacity
          style={[styles.addButton, styles.row]}
          accessibilityLabel="Add Your Own"
          onPress={handleAddPrompt}
        >
          <Icon name="plus" size="16" weight="bold" />
          <Text type="regular" bold>
            Add Your Own
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </NavigationContainer>
  )
}

const themedStyle = StyleService.create({
  contentContainer: {
    flex: 1,
  },
  row: {
    flexDirection: 'row',
  },
  title: {
    alignItems: 'center',
    gap: 8,
  },
  addButton: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: 16,
    backgroundColor: 'theme.surface.base3',
    marginVertical: 12,
    borderRadius: 16,
    height: 70,
  },
  promptsContainer: {
    gap: 12,
  },
  promptCard: {
    backgroundColor: 'theme.background',
    padding: 16,
    borderRadius: 16,
    borderColor: 'theme.surface.base1',
    borderWidth: 1,
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  promptContent: {
    flex: 1,
    gap: 4,
  },
  promptText: {
    color: 'theme.text.secondary',
  },
  link: {
    color: 'theme.text.link',
  },
  deleteAction: {
    backgroundColor: 'theme.error.base',
    justifyContent: 'center',
    alignItems: 'center',
    width: 80,
    borderRadius: 16,
    marginLeft: 12,
  },
  deleteIcon: {
    tintColor: 'theme.solid.white',
  },
})
